import React, { useEffect, useRef, useState } from 'react';
import { API } from '../lib/api';

let scriptPromise = null;
const loadGoogleScript = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.accounts?.id) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = (e) => { scriptPromise = null; reject(e); };
    document.head.appendChild(s);
  });
  return scriptPromise;
};

let cachedConfig = null;
const fetchClientConfig = async () => {
  if (cachedConfig) return cachedConfig;
  try {
    const r = await fetch(`${API}/auth/google/config`);
    cachedConfig = await r.json();
  } catch {
    cachedConfig = { enabled: false, client_id: '' };
  }
  return cachedConfig;
};

/**
 * Drop-in "Sign in with Google" button. Renders nothing when Google auth
 * is not configured in admin Site Settings. On success, calls `onCredential`
 * with the Google-issued ID-token JWT — caller forwards it to the backend.
 */
const GoogleSignInButton = ({ onCredential, text = 'signin_with', size = 'large', disabled, contextKey }) => {
  const wrapRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await fetchClientConfig();
        if (cancelled) return;
        setConfig(cfg);
        if (!cfg.enabled || !cfg.client_id) return;
        await loadGoogleScript();
        if (cancelled || !wrapRef.current) return;
        // Cancel any in-flight One Tap so previous callback doesn't fire here.
        try { window.google.accounts.id.cancel(); } catch (err) { console.debug('GoogleSignInButton: cancel() noop', err); }
        window.google.accounts.id.initialize({
          client_id: cfg.client_id,
          callback: (resp) => {
            if (resp?.credential) onCredential(resp.credential);
          },
          ux_mode: 'popup',
          auto_select: false,
          itp_support: true,
        });
        // Clear any previously-rendered Google button before re-rendering.
        // Using replaceChildren() instead of innerHTML='' avoids static-analysis
        // XSS flags (no markup parsing, no HTML execution).
        wrapRef.current.replaceChildren();
        window.google.accounts.id.renderButton(wrapRef.current, {
          theme: 'outline', size, text, shape: 'rectangular', width: 320,
        });
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load Google');
      }
    })();
    return () => { cancelled = true; };
  }, [onCredential, size, text, contextKey]);

  if (!config) return null;
  if (!config.enabled || !config.client_id) return null;
  if (error) return <div className="text-xs text-red-600" data-testid="google-signin-error">{error}</div>;

  return (
    <div className="flex justify-center" data-testid="google-signin-button">
      <div
        ref={wrapRef}
        className={disabled ? 'pointer-events-none opacity-50' : ''}
        aria-disabled={disabled || undefined}
      />
    </div>
  );
};

export default GoogleSignInButton;
